import { match } from 'ts-pattern';

import {
  CustomPropertyEntityTypeEnum,
  CustomPropertyModel,
  ExportDataEntityEnum,
  ViewColumnMappingEntityEnum,
  ViewColumnMappingTypeEnum,
} from '../../../../shared/models';
import { generateViewColumnKeyFromMapping, ViewColumn } from '../../view';

// Generic interface for export column definition - uses viewColumn.label
export interface ExportColumnDefinition<TUniqueKey extends string = string> {
  uniqueKey: TUniqueKey;
  sourceEntity: ExportDataEntityEnum;
  viewColumn: ViewColumn;
}

// Utility type to extract the keys from a keys array type
export type ExtractKeys<T extends readonly string[]> = T[number];

export function generateCustomPropertyExportColumnUniqueKey(
  entity: ViewColumnMappingEntityEnum,
  key: string,
): string {
  return `${getEntityPrefix(entity)}_custom_properties.${key}`;
}

// --- CUSTOM PROPERTIES EXPORT HELPERS ---
/**
 * Build export column definitions for custom properties
 * @param customProperties Array of custom properties from the repository
 * @param entity The entity type for prefix generation
 * @returns Array of export column definitions for custom properties
 */
export function buildCustomPropertyExportColumns(
  customProperties: CustomPropertyModel[],
  entity: ExportDataEntityEnum,
): ExportColumnDefinition[] {
  const exportEntity = mapViewEntityToCustomPropertyEntity(entity);

  return customProperties.map((customProperty) => ({
    uniqueKey: generateCustomPropertyExportColumnUniqueKey(
      entity,
      customProperty.key,
    ),
    sourceEntity: exportEntity,
    // Use the correct view column mapping key format
    viewColumn: {
      type: customProperty.type,
      mapping: {
        type: ViewColumnMappingTypeEnum.custom,
        entity: customProperty.targetEntity as ViewColumnMappingEntityEnum,
        key: generateViewColumnKeyFromMapping({
          entity: customProperty.targetEntity as ViewColumnMappingEntityEnum,
          type: ViewColumnMappingTypeEnum.custom,
          key: customProperty.key,
        }),
      },
      label: customProperty.label,
      key: generateViewColumnKeyFromMapping({
        entity: customProperty.targetEntity as ViewColumnMappingEntityEnum,
        type: ViewColumnMappingTypeEnum.custom,
        key: customProperty.key,
      }),
      canSort: false,
      canFilter: false,
    } as ViewColumn,
  }));
}

/**
 * Get the appropriate entity prefix for custom property unique keys
 */
function getEntityPrefix(entity: ViewColumnMappingEntityEnum): string {
  return entity.toLowerCase();
}

/**
 * Map view column entity enum to custom property entity type enum
 */
export function mapViewEntityToCustomPropertyEntity(
  viewEntity: ViewColumnMappingEntityEnum,
): CustomPropertyEntityTypeEnum {
  const mapping: Partial<
    Record<ViewColumnMappingEntityEnum, CustomPropertyEntityTypeEnum>
  > = {
    [ViewColumnMappingEntityEnum.case]: CustomPropertyEntityTypeEnum.case,
    [ViewColumnMappingEntityEnum.company]: CustomPropertyEntityTypeEnum.company,
    [ViewColumnMappingEntityEnum.individual]:
      CustomPropertyEntityTypeEnum.individual,
    // Note: check is not available in CustomPropertyEntityTypeEnum
  };

  const result = mapping[viewEntity];
  if (!result) {
    throw new Error(
      `No custom property entity mapping for view entity: ${viewEntity}`,
    );
  }
  return result;
}

/**
 * Get all relevant custom property entity types for a given view entity
 * Some views include multiple entity types (e.g., individual view includes case data)
 */
export function getRelevantCustomPropertyEntities(
  exportEntity: ExportDataEntityEnum,
): CustomPropertyEntityTypeEnum[] {
  return match(exportEntity)
    .with(ExportDataEntityEnum.check, () => [
      CustomPropertyEntityTypeEnum.individual,
      CustomPropertyEntityTypeEnum.company,
      CustomPropertyEntityTypeEnum.case,
    ])
    .with(ExportDataEntityEnum.case, () => [CustomPropertyEntityTypeEnum.case])
    .with(ExportDataEntityEnum.company, () => [
      CustomPropertyEntityTypeEnum.company,
      CustomPropertyEntityTypeEnum.case,
    ])
    .with(ExportDataEntityEnum.individual, () => [
      CustomPropertyEntityTypeEnum.individual,
      CustomPropertyEntityTypeEnum.case,
    ])
    .exhaustive();
}
